import { ROLES } from "../config/roles";
import { getPermissionsForRole } from "../config/permissions";
import { normalizeOpsUser } from "../utils/normalizeOpsUser";

/**
 * Five demo accounts — one per Operations role — shaped like backend `admins`
 * so mock login stores the same Redux fields as the live API.
 */
const MOCK_USERS = [
  {
    id: 1,
    first_name: "Ama",
    last_name: "Ofori",
    email: "admin@simcollect.gh",
    phone_number: "233241112233",
    admin_slug: "adm_001",
    role_slug: "role_super_admin",
    role: ROLES.SUPER_ADMIN,
    status: true,
    last_login_at: "2026-08-07T09:15:00.000000Z",
    created_at: "2025-01-10T10:00:00.000000Z",
    deleted_at: null,
  },
  {
    id: 2,
    first_name: "Nana",
    last_name: "Yaw Boadi",
    email: "exec@simcollect.gh",
    phone_number: "233241112244",
    admin_slug: "exe_001",
    role_slug: "role_executive_manager",
    role: ROLES.EXECUTIVE_MANAGER,
    status: true,
    last_login_at: "2026-08-05T14:00:00.000000Z",
    created_at: "2025-02-01T10:00:00.000000Z",
    deleted_at: null,
  },
  {
    id: 3,
    first_name: "Efua",
    last_name: "Adom",
    email: "manager@simcollect.gh",
    phone_number: "233241112255",
    admin_slug: "ccm_001",
    role_slug: "role_call_center_manager",
    role: ROLES.CALL_CENTER_MANAGER,
    status: true,
    last_login_at: "2026-08-07T08:42:00.000000Z",
    created_at: "2025-03-04T10:00:00.000000Z",
    deleted_at: null,
  },
  {
    id: 4,
    first_name: "Grace",
    last_name: "Mensah",
    email: "agent@simcollect.gh",
    phone_number: "233241112266",
    admin_slug: "agt_01",
    role_slug: "role_call_center_agent",
    role: ROLES.CALL_CENTER_AGENT,
    status: true,
    last_login_at: "2026-08-06T17:20:00.000000Z",
    created_at: "2025-05-18T10:00:00.000000Z",
    deleted_at: null,
  },
  {
    id: 5,
    first_name: "Kofi",
    last_name: "Annan",
    email: "legal@simcollect.gh",
    phone_number: "233241112277",
    admin_slug: "leg_001",
    role_slug: "role_legal_officer",
    role: ROLES.LEGAL_OFFICER,
    status: true,
    last_login_at: null,
    created_at: "2026-08-01T10:00:00.000000Z",
    deleted_at: null,
  },
  {
    id: 6,
    first_name: "Inactive",
    last_name: "Agent",
    email: "inactive.agent@simcollect.gh",
    phone_number: "233241112288",
    admin_slug: "agt_inactive",
    role_slug: "role_call_center_agent",
    role: ROLES.CALL_CENTER_AGENT,
    status: false,
    last_login_at: "2026-07-12T11:20:00.000000Z",
    created_at: "2025-09-01T10:00:00.000000Z",
    deleted_at: null,
    status_reason: "Account deactivated by Super Admin pending HR clearance.",
  },
];

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

let activeOtp = null;
let activeOtpEmail = null;

function generateOtp() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function findUser(email) {
  return MOCK_USERS.find((u) => u.email === email);
}

/**
 * Step 1 — Request a one-time password for passwordless login.
 * @param {string} email
 */
export async function mockRequestLoginOtp(email) {
  await delay(900);

  const normalizedEmail = email.trim().toLowerCase();

  if (!normalizedEmail.includes("@")) {
    throw new Error("Enter a valid work email address.");
  }

  if (!findUser(normalizedEmail)) {
    throw new Error("No ops account found with this email address.");
  }

  activeOtp = generateOtp();
  activeOtpEmail = normalizedEmail;

  const user = findUser(normalizedEmail);

  return {
    email: normalizedEmail,
    channel: "email",
    expires_in: 600,
    demo_otp: activeOtp,
    message: "OTP sent to your email.",
    // Mirror live request payload: operator profile without token
    ...user,
    permissions: getPermissionsForRole(user.role),
  };
}

/**
 * Step 2 — Verify OTP and return auth token + admin-shaped user.
 * @param {string} email
 * @param {string} otp
 */
export async function mockVerifyLoginOtp(email, otp) {
  await delay(900);

  const normalizedEmail = email.trim().toLowerCase();

  if (normalizedEmail !== activeOtpEmail) {
    throw new Error("Session expired. Please request a new verification code.");
  }

  if (String(otp).trim() !== activeOtp) {
    throw new Error("Invalid verification code. Please try again.");
  }

  activeOtp = null;
  activeOtpEmail = null;

  const raw = findUser(normalizedEmail);
  const lastLoginAt = new Date().toISOString();
  const payload = {
    ...raw,
    last_login_at: lastLoginAt,
    permissions: getPermissionsForRole(raw.role),
    token: "mock-jwt-token-" + Date.now(),
    token_type: "Bearer",
    expires_in: 86400,
  };

  // Same shape as live verify: flat actor + token fields
  return payload;
}

/**
 * Resend OTP for the current login session.
 * @param {string} email
 */
export async function mockResendLoginOtp(email) {
  if (email.trim().toLowerCase() !== activeOtpEmail) {
    throw new Error("Session expired. Please start again.");
  }
  return mockRequestLoginOtp(email);
}

export function getMockCredentialsHint() {
  return {
    email: MOCK_USERS[0].email,
    note: "A demo OTP popup appears after you request a code.",
  };
}

/** All demo accounts, one per role — used by the login page's demo picker. */
export function getMockDemoAccounts() {
  return MOCK_USERS.map((raw) => {
    const user = normalizeOpsUser({
      ...raw,
      permissions: getPermissionsForRole(raw.role),
    });
    return {
      email: user.email,
      name: user.name,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      status: formatStatusForDemo(user.status),
      permissions: user.permissions,
    };
  });
}

function formatStatusForDemo(status) {
  if (status === true || status === 1 || status === "active") return "active";
  if (status === false || status === 0 || status === "inactive") return "inactive";
  return String(status ?? "active");
}
